import { readQueueAttentionDetails } from './queueHealth.js';
import { getDeviceId } from '../db/local.js';
import { dbg } from '../debug/debugLog.js';
import {
  shouldEmitSyncHealth,
  syncHealthFingerprint,
  type PreviousSyncHealthReport,
  type SyncHealthSnapshot,
} from './syncHealthPolicy.js';

export type { SyncHealthSnapshot } from './syncHealthPolicy.js';

const PERIODIC_REPORT_MS = 15 * 60 * 1000;

let previousReport: PreviousSyncHealthReport | null = null;
let lastObservedAt = 0;

/**
 * Best-effort by design: observability must never become a sync dependency.
 * A snapshot is emitted only on state changes or every 15 minutes.
 */
export async function emitSyncHealth(snapshot: SyncHealthSnapshot): Promise<void> {
  try {
    const now = Date.now();
    // A corrected far-future device clock must not latch rejected telemetry.
    const clockReset = lastObservedAt > now + 2 * 60 * 1000;
    if (clockReset) previousReport = null;
    const fingerprint = syncHealthFingerprint(snapshot);
    if (!shouldEmitSyncHealth(previousReport, fingerprint, now, PERIODIC_REPORT_MS)) return;

    // Capture before the SQLite/queue awaits: logger insertion can be delayed
    // and legacy SQLite timestamps only have second precision.
    lastObservedAt = clockReset ? now : Math.max(now, lastObservedAt + 1);
    const observedAt = new Date(lastObservedAt).toISOString();

    const deviceId = await getDeviceId();
    previousReport = { fingerprint, emittedAt: now };
    const payload = {
      observed_at: observedAt,
      tenant_id: snapshot.tenantId,
      device_id: deviceId,
      client_version:
        (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown',
      sync_protocol: 1,
      status: snapshot.status,
      pending_count: snapshot.pendingCount,
      attention_count: snapshot.attentionCount,
      consecutive_failures: snapshot.consecutiveFailures,
      last_error: snapshot.lastError,
      last_pull_at: snapshot.lastPullAt,
      last_push_at: snapshot.lastPushAt,
      queue_details: snapshot.attentionCount > 0
        ? await readQueueAttentionDetails(snapshot.tenantId).catch(() => undefined)
        : [],
    };
    const hasProblem = snapshot.status === 'error' || snapshot.attentionCount > 0;
    dbg[hasProblem ? 'warn' : 'info']('sync-health', 'snapshot', payload);
  } catch {
    // Telemetria non bloccante: la sincronizzazione resta la sola priorità.
  }
}
